import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { SortableColumnPicker } from "@/components/dataTable";
import {
  DOCUMENT_REQUIRED_COLUMNS,
  getAvailableDocumentColumns,
  getDefaultDocumentColumns,
  getDocumentColumnLabel,
  normalizeDocumentColumnSelection,
  type DocumentColumnKey,
} from "@/pages/dokumente/documentColumns";
import type { DocumentColumnSettings, DocumentColumnView } from "@/pages/dokumente/useDocumentTableSettings";

export interface DocumentColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  settings: DocumentColumnSettings;
  onSave: (next: DocumentColumnSettings) => Promise<void> | void;
  /** Spalten, die in dieser Liste immer sichtbar sind und nicht in die gespeicherte Auswahl wandern. */
  pinnedColumns?: DocumentColumnKey[];
}

function withPinnedColumns(columns: string[], pinned: readonly string[]): string[] {
  const next = [...columns];
  for (const pin of pinned) {
    if (next.includes(pin)) continue;
    const at = next.indexOf("status");
    next.splice(at >= 0 ? at + 1 : next.length, 0, pin);
  }
  return next;
}

function withoutUnsavedPins(
  columns: string[],
  saved: string[] | null | undefined,
  pinned: readonly string[],
): string[] {
  const savedSet = new Set(saved || []);
  return columns.filter((col) => !pinned.includes(col) || savedSet.has(col));
}

type View = "desktop" | "mobile";

const NO_PINNED_COLUMNS: DocumentColumnKey[] = [];

export function DocumentColumnsDialog({
  open,
  onClose,
  settings,
  onSave,
  pinnedColumns = NO_PINNED_COLUMNS,
}: DocumentColumnsDialogProps) {
  const available = useMemo(() => getAvailableDocumentColumns(), []);
  const defaults = useMemo(() => getDefaultDocumentColumns(), []);
  const requiredColumns = useMemo(
    () => [...DOCUMENT_REQUIRED_COLUMNS, ...pinnedColumns],
    [pinnedColumns],
  );
  const [tab, setTab] = useState<View>("desktop");
  const [desktop, setDesktop] = useState<string[]>(() =>
    normalizeDocumentColumnSelection(settings.desktop?.columns, available),
  );
  const [mobile, setMobile] = useState<string[]>(() =>
    normalizeDocumentColumnSelection(settings.mobile?.columns, available),
  );
  const [saving, setSaving] = useState(false);
  const [baselineDesktop, setBaselineDesktop] = useState<string[]>(desktop);
  const [baselineMobile, setBaselineMobile] = useState<string[]>(mobile);

  useEffect(() => {
    if (!open) return;
    const nextDesktop = withPinnedColumns(
      settings.desktop?.columns?.length
        ? normalizeDocumentColumnSelection(settings.desktop.columns, available)
        : defaults,
      pinnedColumns,
    );
    const nextMobile = withPinnedColumns(
      settings.mobile?.columns?.length
        ? normalizeDocumentColumnSelection(settings.mobile.columns, available)
        : defaults,
      pinnedColumns,
    );
    setDesktop(nextDesktop);
    setMobile(nextMobile);
    setBaselineDesktop(nextDesktop);
    setBaselineMobile(nextMobile);
  }, [open, settings, available, defaults, pinnedColumns]);

  const current = tab === "desktop" ? desktop : mobile;
  const setCurrent = tab === "desktop" ? setDesktop : setMobile;

  async function handleSave() {
    setSaving(true);
    try {
      const desktopView: DocumentColumnView = {
        columns: normalizeDocumentColumnSelection(
          withoutUnsavedPins(desktop, settings.desktop?.columns, pinnedColumns),
          available,
        ),
        widths: settings.desktop?.widths ?? {},
      };
      const mobileView: DocumentColumnView = {
        columns: normalizeDocumentColumnSelection(
          withoutUnsavedPins(mobile, settings.mobile?.columns, pinnedColumns),
          available,
        ),
        widths: settings.mobile?.widths ?? {},
      };
      await onSave({ desktop: desktopView, mobile: mobileView });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const dirty = useMemo(
    () =>
      hasFormChanges(desktop, baselineDesktop) || hasFormChanges(mobile, baselineMobile),
    [desktop, baselineDesktop, mobile, baselineMobile],
  );

  const requestClose = useDialogRequestClose(onClose);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Spalten anpassen"
      description="Wähle Dokument- und Projektfelder (z. B. Netto, Ansprechpartner) und ziehe sie per Drag-and-Drop in die gewünschte Reihenfolge. Die Spaltenbreiten speichern sich beim Ziehen am Tabellenkopf."
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => setCurrent(withPinnedColumns(defaults, pinnedColumns))}
            disabled={saving}
          >
            Standard wiederherstellen
          </Button>
          <Button
            variant="secondary"
            onClick={() => setCurrent(withPinnedColumns(available, pinnedColumns))}
            disabled={saving}
          >
            Alle auswählen
          </Button>
          <Button variant="ghost" onClick={requestClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} loading={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="mb-3 flex gap-1 rounded-full bg-fill-tertiary p-1 text-sm">
        <TabButton active={tab === "desktop"} onClick={() => setTab("desktop")}>
          Desktop
        </TabButton>
        <TabButton active={tab === "mobile"} onClick={() => setTab("mobile")}>
          Mobil
        </TabButton>
      </div>

      {pinnedColumns.length > 0 ? (
        <p className="mb-3 text-sm text-label-secondary">
          In der Rechnungsliste steht der Zahlungsverlauf: wer eine Zahlung erfasst, geändert oder gelöscht hat.
        </p>
      ) : null}

      <SortableColumnPicker
        available={available}
        selected={current}
        requiredColumns={requiredColumns}
        getLabel={getDocumentColumnLabel}
        onSelectedChange={setCurrent}
        disabled={saving}
      />
    </Dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex-1 rounded-full px-3 py-1.5 font-medium transition",
        active ? "bg-card text-label shadow-sm" : "text-label-secondary hover:text-label",
      )}
    >
      {children}
    </button>
  );
}
